import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import Image from "mui-image";
import { StyledTooltip } from "Components/UI/StyledTooltip";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { TTableCustomCellRender } from "features/analytics/types/types";
import {
  findProductByName,
  formatRevenueGrowthPercent,
  getProductCategoryLabel,
  PRODUCT_REVENUE_GROWTH_FIELD,
  PRODUCT_REVENUE_TREND_FIELD,
  revenueChangePercent,
} from "features/products/utils/productUtils";
import ProductTrendSparkline from "features/products/components/ProductTrendSparkline";
import {
  buildProductPerformanceChartFields,
  getProductPerformanceTabConfig,
  ProductPerformanceTab,
} from "features/products/utils/productPerformanceTabs";
import { useProducts } from "Hooks/useProducts";
import { useSelectedProject } from "Hooks";
import { useIsMobile } from "Hooks/useIsMobile";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useCallback, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { TChartData } from "Interfaces";
import { base64ToArrayBuffer, downloadCsv } from "Helpers/download";
import { parseIChartToChartPayload } from "features/chartLibrary/utils";
import { exportStandardChart } from "features/chartLibrary/services/chartService";
import { useFromToContext } from "Providers/FromToProvider";

const EMPTY_COMPARISON_ROWS: TChartData[] = [];

interface IProductPerformanceTableProps {
  chartAttributes?: ApiChartAttribute[];
  tab?: ProductPerformanceTab;
  excludedDimensions?: ApiDimension[];
  excludedMetrics?: ApiMetric[];
}

function ProductPerformanceTable({
  chartAttributes = [],
  tab = "top_sellers",
  excludedDimensions = [],
  excludedMetrics = [],
}: IProductPerformanceTableProps) {
  const history = useHistory();
  const { products } = useProducts();
  const { selectedProject, selectedClient } = useSelectedProject();
  const { pathByName } = useProtectedRoutes();
  const tabConfig = useMemo(() => getProductPerformanceTabConfig(tab), [tab]);
  const isMobile = useIsMobile();
  const { chartOutput } = useFromToContext();
  const comparisonDataRef = useRef<TChartData[]>([]);
  const [comparisonRevision, setComparisonRevision] = useState(0);
  const chartFields = useMemo(
    () => buildProductPerformanceChartFields(tabConfig),
    [tabConfig],
  );
  const tableHiddenCols = useMemo(
    () => (tabConfig.ranks?.length ? [ApiMetric.METRIC_RANK] : undefined),
    [tabConfig],
  );

  const toProduct = useCallback(
    (productName: string) => {
      const productId = products.find((p) => p.name === productName)?.id;
      if (!productId || !selectedProject?.id || !selectedClient?.id) {
        return;
      }

      history.push(
        pathByName(RouteNames.PRODUCT_DEEP_DIVE, {
          clientId: selectedClient.id,
          projectId: selectedProject.id,
        }),
        { selectedProductId: productId },
      );
    },
    [history, pathByName, products, selectedClient?.id, selectedProject?.id],
  );

  const tableCustomCellRender = useMemo<TTableCustomCellRender>(
    () => ({
      [ApiDimension.DIMENSION_PRODUCT_NAME]: ({ value, data }) => {
        const name = String(value || data?.name || "");
        const product = findProductByName(products, name);

        return (
          <StyledTooltip
            placement="right"
            arrow
            enterDelay={700}
            title={
              <>
                <Stack px={2.5}>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    mt={0.5}
                    gutterBottom
                  >
                    Product
                  </Typography>
                  <Divider />
                  <Stack alignItems="center" mt={1} mb={1.5}>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {name}
                    </Typography>
                  </Stack>
                </Stack>
                <ButtonGroup
                  orientation="vertical"
                  variant="contained"
                  fullWidth
                >
                  <Button
                    size="extraSmall"
                    color="primary"
                    fullWidth
                    onClick={() => toProduct(name)}
                  >
                    Go To Product
                  </Button>
                </ButtonGroup>
              </>
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                height: "100%",
                minWidth: 0,
              }}
            >
              {!isMobile ? (
                <>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      overflow: "hidden",
                      flexShrink: 0,
                      bgcolor: "grey.50",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {product?.imageUrl ? (
                      <Image
                        src={product.imageUrl}
                        alt={name}
                        height={40}
                        width={40}
                        fit="cover"
                        duration={0}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </Box>
                  <Typography fontWeight={600} noWrap title={name}>
                    {name}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ textWrap: "pretty" }}
                  title={name}
                >
                  {name}
                </Typography>
              )}
            </Box>
          </StyledTooltip>
        );
      },
      [ApiDimension.DIMENSION_PRODUCT_CATEGORY]: ({ value }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{String(value || "—")}</Typography>
        </Box>
      ),
      [PRODUCT_REVENUE_GROWTH_FIELD]: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography>
            {formatRevenueGrowthPercent(Number(value || 0))}
          </Typography>
        </Box>
      ),
      [PRODUCT_REVENUE_TREND_FIELD]: ({ data }) => {
        const productName = String(
          data?.name || data?.[ApiDimension.DIMENSION_PRODUCT_NAME] || "",
        );

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              minWidth: 100,
            }}
          >
            <ProductTrendSparkline
              productName={productName}
              trendMetric={ApiMetric.METRIC_PRODUCT_REVENUE}
              height={36}
              loadingSize={20}
            />
          </Box>
        );
      },
    }),
    [products, toProduct, isMobile],
  );

  const tableNonSortableCols = useMemo(() => [PRODUCT_REVENUE_TREND_FIELD], []);

  const tableCustomHeaderRender = useMemo<TTableCustomCellRender>(
    () => ({
      [ApiDimension.DIMENSION_PRODUCT_NAME]: () => <>Product</>,
      [ApiDimension.DIMENSION_PRODUCT_CATEGORY]: () => <>Category</>,
      [ApiMetric.METRIC_PRODUCT_GATEWAY_LTV]: () => (
        <Typography
          align="center"
          color="text.secondary"
          fontSize={12}
          fontWeight={600}
        >
          Gateway LTV
        </Typography>
      ),
      [ApiMetric.METRIC_RETURNING_CUSTOMER_RATE]: () => (
        <Typography
          align="center"
          color="text.secondary"
          fontSize={12}
          fontWeight={600}
        >
          Repeat Rate
        </Typography>
      ),
      [ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT]: () => (
        <Typography
          align="center"
          color="text.secondary"
          fontSize={12}
          fontWeight={600}
        >
          Repeat Buyers
        </Typography>
      ),
      [ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE]: () => (
        <Typography
          align="center"
          color="text.secondary"
          fontSize={12}
          fontWeight={600}
        >
          Product page CVR
        </Typography>
      ),
      [ApiMetric.METRIC_SALE_ITEMS_QUANTITY]: () => (
        <Typography
          align="center"
          color="text.secondary"
          fontSize={12}
          fontWeight={600}
        >
          Units Sold
        </Typography>
      ),
      [PRODUCT_REVENUE_GROWTH_FIELD]: () => (
        <Typography
          align="center"
          color="text.secondary"
          fontSize={12}
          fontWeight={600}
        >
          Revenue Growth
        </Typography>
      ),
      [PRODUCT_REVENUE_TREND_FIELD]: () => (
        <Typography
          align="center"
          color="text.secondary"
          fontSize={12}
          fontWeight={600}
        >
          Revenue Trend
        </Typography>
      ),
    }),
    [],
  );

  const tableColWidths = useMemo(
    () => ({
      [ApiDimension.DIMENSION_PRODUCT_NAME]: isMobile ? 125 : 320,
      [ApiDimension.DIMENSION_PRODUCT_CATEGORY]: 280,
      [ApiMetric.METRIC_PRODUCT_GATEWAY_LTV]: isMobile ? 100 : 130,
      [ApiMetric.METRIC_RETURNING_CUSTOMER_RATE]: isMobile ? 100 : 130,
      [ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT]: isMobile ? 100 : 150,
      [ApiMetric.METRIC_PRODUCT_REVENUE]: isMobile ? 100 : 130,
      [ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE]: isMobile ? 100 : 130,
      [ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT]: isMobile ? 100 : 110,
      [ApiMetric.METRIC_SALE_ITEMS_QUANTITY]: isMobile ? 100 : 110,
      [PRODUCT_REVENUE_GROWTH_FIELD]: isMobile ? 100 : 120,
      [PRODUCT_REVENUE_TREND_FIELD]: 130,
    }),
    [isMobile],
  );

  const mapTableRow = useCallback(
    (row: TChartData): TChartData => {
      const productName = String(row.name || "");
      const product = findProductByName(products, productName);

      const rowData = {
        name: row.name,
        [ApiDimension.DIMENSION_PRODUCT_NAME]: row.name,
        [ApiDimension.DIMENSION_PRODUCT_CATEGORY]:
          getProductCategoryLabel(product),
        ...Object.fromEntries(chartFields.map((field) => [field, row[field]])),
        [PRODUCT_REVENUE_TREND_FIELD]: null,
      };

      return Object.fromEntries(
        Object.entries(rowData).filter(
          ([key]) =>
            !excludedDimensions.includes(key as ApiDimension) &&
            !excludedMetrics.includes(key as ApiMetric),
        ),
      ) as TChartData;
    },
    [chartFields, products, excludedDimensions, excludedMetrics],
  );

  const enrichWithRisingStarsGrowth = useCallback(
    (rows: ReturnType<typeof mapTableRow>[]) => {
      const comparisonRows = comparisonDataRef.current;
      if (!comparisonRows.length) {
        return rows;
      }

      return rows
        .map((row) => {
          const productName = String(row.name || "");
          const comparisonRevenue = Number(
            comparisonRows.find(
              (compRow) => String(compRow.name || "") === productName,
            )?.[ApiMetric.METRIC_PRODUCT_REVENUE] || 0,
          );
          const revenue = Number(row[ApiMetric.METRIC_PRODUCT_REVENUE] || 0);

          return {
            ...row,
            [PRODUCT_REVENUE_GROWTH_FIELD]: revenueChangePercent(
              revenue,
              comparisonRevenue,
            ),
          };
        })
        .sort(
          (a, b) =>
            Number(b[PRODUCT_REVENUE_GROWTH_FIELD] || 0) -
            Number(a[PRODUCT_REVENUE_GROWTH_FIELD] || 0),
        );
    },
    [],
  );

  const customDataFn = useCallback(
    (rows: TChartData[]) => {
      void comparisonRevision;
      const mapped = rows.map((row) => mapTableRow(row));
      const isComparisonPass =
        comparisonDataRef.current.length > 0 &&
        rows === comparisonDataRef.current;

      if (tab === "rising_stars" && !isComparisonPass) {
        return enrichWithRisingStarsGrowth(mapped);
      }

      return mapped;
    },
    [comparisonRevision, enrichWithRisingStarsGrowth, mapTableRow, tab],
  );

  const onDataChange = useCallback(
    (_: TChartData[], comp?: TChartData[]) => {
      const next = comp ?? EMPTY_COMPARISON_ROWS;
      if (comparisonDataRef.current === next) {
        return;
      }

      comparisonDataRef.current = next;
      if (tab === "rising_stars") {
        setComparisonRevision((revision) => revision + 1);
      }
    },
    [tab],
  );

  const tableClickHandlers = useMemo(
    () => ({
      [ApiDimension.DIMENSION_PRODUCT_NAME]: [
        {
          text: "Go To Product",
          fn: (productName?: string) => toProduct(productName || ""),
        },
      ],
    }),
    [toProduct],
  );

  const chart = useMemo(
    () => ({
      name: "Product Performance Table",
      dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
      template: ApiChartTemplate.TABLE,
      type: ApiChartType.TABLE,
      attributes: chartAttributes,
      orderBy: tabConfig.orderBy,
      having: tabConfig.having,
      ...(tabConfig.fetchLimit ? { limit: tabConfig.fetchLimit } : {}),
      ...(tabConfig.ranks?.length ? { ranks: tabConfig.ranks } : {}),
      yAxisKeys: [
        {
          key: "l",
          fields: chartFields,
        },
      ],
      displayOptions: {
        noAttributionToggle: true,
        noFilters: true,
        rowsPerPage: 0,
        tableColWidths,
      },
    }),
    [
      chartAttributes,
      chartFields,
      tabConfig.fetchLimit,
      tabConfig.having,
      tabConfig.orderBy,
      tabConfig.ranks,
      tableColWidths,
    ],
  );

  const onChartExport = useCallback(async () => {
    if (!selectedProject?.id) {
      return;
    }
    const res = await exportStandardChart(
      selectedProject?.id,
      parseIChartToChartPayload(chart),
      {
        ...chartOutput,
      },
    );
    if (res?.result?.result) {
      const buffer = base64ToArrayBuffer(res.result.result);
      downloadCsv(buffer, chart.name || "Chart Export");
    }
  }, [selectedProject?.id, chart, chartOutput]);

  return (
    <AnalyticsCard
      onExport={onChartExport}
      onClick={tableClickHandlers}
      tableCustomCellRender={tableCustomCellRender}
      tableCustomHeaderRender={tableCustomHeaderRender}
      tableHiddenCols={tableHiddenCols}
      tableNonSortableCols={tableNonSortableCols}
      onDataChange={onDataChange}
      customDataFn={customDataFn}
      chart={chart}
    />
  );
}

export default ProductPerformanceTable;
